import {
  Position,
  PlayerFacing,
  BeltItem,
  ItemType,
  BuildingType,
  Building,
  GAME_CONFIG,
} from './constants';
import {
  BuildingLayer,
  BeltContentLayer,
  MinerTimerLayer,
  findBuildingByCharacter,
  getBuildingAtPosition,
} from './GameState';
import { TickSystem, getCurrentTick } from './hooks/useGameTick';
import { beltLogger } from './Logger';
import { getBeltDirectionFromCharacter } from './SmartBeltPlacement';
import { secondsToTicks, ticksToSeconds } from './utils/TickIntervals';

// Generate unique ID for belt items
const resolveCurrentTick = (tickSystem?: TickSystem): number => {
  return tickSystem?.getCurrentTick() ?? getCurrentTick();
};

export const generateDefaultBeltItemId = (tickSystem?: TickSystem): string => {
  const currentTick = resolveCurrentTick(tickSystem);
  const randomComponent = Math.random().toString(36).slice(2, 11);
  return `item_${currentTick}_${randomComponent}`;
};

const BASE_MINER_OUTPUT_SECONDS = 3;

const getEffectiveMinerSpeedMultiplier = (
  speedMultiplier: number,
  building?: Building | null
): number => {
  const normalizedGlobal = Math.max(0.1, speedMultiplier);
  const buildingMultiplier = Math.max(
    0.1,
    building?.miningSpeedMultiplier ?? 1
  );
  return Math.max(0.1, normalizedGlobal * buildingMultiplier);
};

const getMinerOutputIntervalTicksForMultiplier = (
  speedMultiplier: number,
  building?: Building | null
): number => {
  const effectiveMultiplier = getEffectiveMinerSpeedMultiplier(
    speedMultiplier,
    building
  );
  const baseIntervalTicks = secondsToTicks(BASE_MINER_OUTPUT_SECONDS);
  return Math.max(1, Math.round(baseIntervalTicks / effectiveMultiplier));
};

// Calculate miner output interval in seconds based on speed multiplier
export const getMinerOutputIntervalSeconds = (
  speedMultiplier: number = 1,
  building?: Building | null
): number => {
  const minerOutputIntervalTicks = getMinerOutputIntervalTicksForMultiplier(
    speedMultiplier,
    building
  );
  return ticksToSeconds(minerOutputIntervalTicks);
};

// Get the number of ticks until a miner will output (for testing)
export const getMinerOutputTicks = (
  position: Position,
  minerTimerLayer: MinerTimerLayer,
  speedMultiplier: number = 1,
  building?: Building | null,
  tickSystem?: TickSystem
): number => {
  const currentTick = resolveCurrentTick(tickSystem);
  const minerOutputIntervalTicks = getMinerOutputIntervalTicksForMultiplier(
    speedMultiplier,
    building
  );
  const posKey = `${position.x},${position.y}`;
  const minerStartTick = minerTimerLayer[posKey];

  if (minerStartTick === undefined) {
    // New miner - will output after full interval from when it's placed
    return minerOutputIntervalTicks;
  }

  if (minerStartTick > currentTick) {
    // Invalid timer from previous session - will output after full interval from now
    return minerOutputIntervalTicks;
  }

  // Calculate when the next output should happen
  const nextOutputTick = minerStartTick + minerOutputIntervalTicks;
  const ticksUntilOutput = nextOutputTick - currentTick;

  return Math.max(0, ticksUntilOutput);
};

// Get item type based on what the miner is placed on
export const getMinerOutputType = (
  tile: string | undefined,
  building?: Building | null
): ItemType | null => {
  if (building?.id === BuildingType.SAND_DREDGER) {
    return 'sand';
  }
  switch (tile) {
    case GAME_CONFIG.IRON_NODE_CHARACTER:
      return 'iron-ore';
    case GAME_CONFIG.COPPER_NODE_CHARACTER:
      return 'copper-ore';
    default:
      return null;
  }
};

// Get adjacent positions in all directions
export const getAdjacentPositions = (
  pos: Position
): { position: Position; direction: PlayerFacing }[] => {
  return [
    { position: { x: pos.x, y: pos.y - 1 }, direction: 'up' as PlayerFacing },
    { position: { x: pos.x, y: pos.y + 1 }, direction: 'down' as PlayerFacing },
    { position: { x: pos.x - 1, y: pos.y }, direction: 'left' as PlayerFacing },
    {
      position: { x: pos.x + 1, y: pos.y },
      direction: 'right' as PlayerFacing,
    },
  ];
};

// Check if a position has a belt
export const hasBelt = (
  position: Position,
  buildingLayer: BuildingLayer
): boolean => {
  const key = `${position.x},${position.y}`;
  const building = buildingLayer[key];
  if (!building) return false;

  // Check if it's any belt character (directional)
  return getBeltDirectionFromCharacter(buildingLayer, position) !== null;
};

// Check if a position has an assembler
export const hasAssembler = (
  position: Position,
  buildingLayer: BuildingLayer
): boolean => {
  const { building } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );
  const isAssembler =
    building?.id === BuildingType.ASSEMBLER ||
    building?.id === BuildingType.FACTORY;

  beltLogger.debug('Checking for assembler at position', {
    position: `${position.x},${position.y}`,
    building: building?.id || 'empty',
    isAssembler,
  });

  return isAssembler;
};

// Find the center position of a multi-tile assembler
export const getAssemblerCenterPosition = (
  position: Position,
  buildingLayer: BuildingLayer
): Position | null => {
  const { building, centerPosition } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );
  if (
    !building ||
    (building.id !== BuildingType.ASSEMBLER &&
      building.id !== BuildingType.FACTORY)
  ) {
    return null;
  }

  return centerPosition;
};

// Add item to belt at specific position
export const addItemToBelt = (
  position: Position,
  itemType: ItemType,
  beltContentLayer: BeltContentLayer,
  maxCapacity: number = 1,
  generateId: () => string = () => generateDefaultBeltItemId()
): BeltContentLayer => {
  const key = `${position.x},${position.y}`;
  const currentItems = beltContentLayer[key] || [];
  const existingStack = currentItems[0];
  const existingCount = existingStack?.count ?? (existingStack ? 1 : 0);

  beltLogger.debug('Adding item to belt', {
    itemType,
    position: key,
    currentCount: existingCount,
    hasStack: Boolean(existingStack),
    maxCapacity,
  });

  if (!existingStack) {
    const newItem: BeltItem = {
      item: itemType,
      progress: 0, // Start at the beginning of the belt
      id: generateId(),
      count: 1,
    };

    beltLogger.info('Created new belt stack', {
      itemType,
      position: key,
      itemId: newItem.id,
      count: 1,
      maxCapacity,
    });

    return {
      ...beltContentLayer,
      [key]: [newItem],
    };
  }

  if (existingStack.item === itemType && existingCount < maxCapacity) {
    const updatedStack: BeltItem = {
      ...existingStack,
      count: Math.min(maxCapacity, existingCount + 1),
    };

    beltLogger.info('Increased belt stack count', {
      itemType,
      position: key,
      itemId: existingStack.id,
      previousCount: existingCount,
      newCount: updatedStack.count,
      maxCapacity,
    });

    return {
      ...beltContentLayer,
      [key]: [updatedStack],
    };
  }

  beltLogger.debug('Belt cannot accept additional items', {
    position: key,
    itemType,
    existingItemType: existingStack?.item ?? null,
    count: existingCount,
    maxCapacity,
  });
  return beltContentLayer; // No change if belt is full or item types differ
};

// Process miner output for all miners
export const processMinerOutputs = (
  buildingLayer: BuildingLayer,
  beltContentLayer: BeltContentLayer,
  minerTimerLayer: MinerTimerLayer,
  gameMap: Record<string, string>,
  getTile: (
    x: number,
    y: number,
    gameMap: Record<string, string>
  ) => string | undefined,
  tickSystem?: TickSystem
): { beltContentLayer: BeltContentLayer; minerTimerLayer: MinerTimerLayer } => {
  let updatedBeltContent = { ...beltContentLayer };
  const updatedMinerTimers = { ...minerTimerLayer };

  const currentTick = resolveCurrentTick(tickSystem);

  // Find all miners in the building layer
  const allBuildings = Object.entries(buildingLayer);
  const allMiners = allBuildings.filter(([, buildingChar]) => {
    const building = findBuildingByCharacter(buildingChar);
    return building && building.category === 'mining';
  });

  beltLogger.debug('Processing miners', {
    totalBuildings: allBuildings.length,
    totalMiners: allMiners.length,
    currentTick,
    minerPositions: allMiners.map(([posKey]) => posKey),
  });

  for (const [posKey, buildingChar] of allBuildings) {
    // Check if this is a miner
    const building = findBuildingByCharacter(buildingChar);
    if (building && building.category === 'mining') {
      const [xStr, yStr] = posKey.split(',');
      if (!xStr || !yStr) continue; // Skip invalid position keys
      const minerPos = { x: parseInt(xStr, 10), y: parseInt(yStr, 10) };

      const minerOutputIntervalTicks = getMinerOutputIntervalTicksForMultiplier(
        1,
        building
      );

      // Check if enough ticks have passed since last output
      const lastOutputTick = updatedMinerTimers[posKey];

      // Handle miner timing - miners should output every MINER_OUTPUT_INTERVAL_TICKS consistently
      let minerStartTick: number;
      if (lastOutputTick === undefined) {
        // New miner - initialize with current tick as start time
        minerStartTick = currentTick;
        updatedMinerTimers[posKey] = minerStartTick;
        beltLogger.info('New miner found, initializing timer', {
          position: posKey,
          minerStartTick,
          currentTick,
          nextOutputTick: minerStartTick + minerOutputIntervalTicks,
        });
        continue; // Skip this cycle, will output after interval
      } else if (lastOutputTick > currentTick) {
        // Previous session timer is invalid - restart with current tick
        minerStartTick = currentTick;
        updatedMinerTimers[posKey] = minerStartTick;
        beltLogger.info('Reset miner timer from previous session', {
          position: posKey,
          oldTimer: lastOutputTick,
          newStartTick: minerStartTick,
          currentTick,
          nextOutputTick: minerStartTick + minerOutputIntervalTicks,
        });
        continue; // Skip this cycle, will output after interval
      } else {
        minerStartTick = lastOutputTick;
      }

      // Check if it's time to output (currentTick >= startTick + interval)
      const nextOutputTick = minerStartTick + minerOutputIntervalTicks;
      if (currentTick < nextOutputTick) {
        beltLogger.debug('Miner not ready to output yet', {
          position: posKey,
          currentTick,
          nextOutputTick,
          ticksRemaining: nextOutputTick - currentTick,
        });
        continue; // Skip this miner
      }

      beltLogger.info('Found miner ready for item output', {
        position: posKey,
        currentTick,
        minerStartTick,
        nextOutputTick,
      });

      // Get the tile the miner is on to determine output type
      const tile = getTile(minerPos.x, minerPos.y, gameMap);
      const outputType = getMinerOutputType(tile, building);

      beltLogger.debug('Miner output analysis', {
        position: posKey,
        tile,
        outputType,
      });

      if (outputType) {
        // Check adjacent positions for belts
        const adjacentPositions = getAdjacentPositions(minerPos);

        for (const { position } of adjacentPositions) {
          const hasBeltResult = hasBelt(position, buildingLayer);
          beltLogger.debug('Checking adjacent position for belt', {
            position: `${position.x},${position.y}`,
            hasBelt: hasBeltResult,
          });

          if (hasBeltResult) {
            // Try to output to this belt
            beltLogger.info('Miner outputting item to belt', {
              minerPos: posKey,
              beltPos: `${position.x},${position.y}`,
              itemType: outputType,
            });
            const nextBeltContent = addItemToBelt(
              position,
              outputType,
              updatedBeltContent,
              1,
              () => generateDefaultBeltItemId(tickSystem)
            );

            if (nextBeltContent === updatedBeltContent) {
              beltLogger.debug('Miner could not stack item on belt', {
                minerPos: posKey,
                beltPos: `${position.x},${position.y}`,
                itemType: outputType,
              });
              continue;
            }

            updatedBeltContent = nextBeltContent;

            // Advance miner timer by the interval for consistent timing
            updatedMinerTimers[posKey] =
              minerStartTick + minerOutputIntervalTicks;
            beltLogger.debug('Updated miner timer', {
              position: posKey,
              previousStartTick: minerStartTick,
              newStartTick: updatedMinerTimers[posKey],
              currentTick,
              nextOutputAt:
                updatedMinerTimers[posKey] + minerOutputIntervalTicks,
            });

            break; // Output to first available belt found
          }
        }
      }
    }
  }

  return {
    beltContentLayer: updatedBeltContent,
    minerTimerLayer: updatedMinerTimers,
  };
};
